import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import { imageUrl } from '../lib/images'

export default function HomePage() {
  return (
    <>
      <SEO
        isHome={true}
        title="Havdrup Rengøring"
        description="Havdrup Rengøring tilbyder fleksibel rengøring til private, virksomheder og klinikker. Kontakt os for en løsning og et tilbud tilpasset din opgave."
      />

      {/* ============================================================ */}
      {/* SECTION 1: FULL-BLEED EDITORIAL HERO                         */}
      {/* ============================================================ */}
      <section className="relative overflow-hidden bg-[#0d3835] text-white min-h-[90vh] lg:min-h-screen flex items-center">
        {/* Full-bleed atmospheric background image continuing across entire section */}
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl(
              '/_assets/shutterstock-2441536685.jpg',
              'width=2000,quality=90,format=auto'
            )}
            alt="Professionel gulvvask og rengøring i lyst moderne hjem med naturligt dagslys"
            className="h-full w-full object-cover object-center lg:object-[65%_center]"
            loading="eager"
          />
          {/* Subtle gradient overlay across the entire hero: fading from stronger opacity on content side into image side */}
          <div
            className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-[#0d3835] via-[#0d3835]/95 sm:via-[#0d3835]/85 lg:via-[#0d3835]/70 to-[#0d3835]/30 lg:to-transparent"
            aria-hidden="true"
          />
        </div>

        {/* 12-column layout container with generous negative space */}
        <div className="relative z-10 w-full mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-28 lg:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center">
            {/* Content block: spanning roughly 5 columns on the left */}
            <div className="lg:col-span-6 xl:col-span-5 flex flex-col justify-center">
              {/* Small uppercase eyebrow line with wide letter spacing */}
              <div className="inline-flex items-center gap-2 mb-5 sm:mb-6">
                <span className="text-xs sm:text-[13px] uppercase tracking-[0.25em] text-emerald-200/90 font-medium">
                  Lokal rengøringsservice i Havdrup &amp; omegn
                </span>
              </div>

              {/* Large elegant sans-serif display heading with generous line height and refined lightweight appearance */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] xl:text-[3.6rem] font-light tracking-tight text-white leading-[1.2] font-display mb-6 sm:mb-8">
                Professionel rengøring til private, erhverv og <span className="text-emerald-200 font-normal">klinikker</span>
              </h1>

              {/* Short muted paragraph - plain text only, no container, no card */}
              <p className="text-base sm:text-lg text-emerald-100/80 leading-relaxed font-light mb-8 sm:mb-10 max-w-xl">
                Hos Havdrup Rengøring får du grundig og fleksibel rengøring tilpasset dine behov. Vi hjælper både private, virksomheder og klinikker og lægger vægt på kvalitet, ordentlighed og gode aftaler.
              </p>

              {/* Minimalist uppercase text links: CTA styled as underlined text link (no button, no icon, no filled background, no border box) */}
              <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
                <Link
                  to="/kontakt"
                  className="inline-block text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-white hover:text-emerald-200 underline underline-offset-8 decoration-white/60 hover:decoration-emerald-200 transition-colors cursor-pointer"
                >
                  Få et tilbud
                </Link>

                <a
                  href="tel:31341632"
                  className="inline-block text-xs sm:text-sm uppercase tracking-[0.18em] font-medium text-emerald-100/80 hover:text-white underline underline-offset-8 decoration-emerald-200/40 hover:decoration-white transition-colors cursor-pointer"
                >
                  <span className="no-break">Ring på 31 34 16 32</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 2: FLOATING VALUE PROPOSITION CARDS (CLEANER-10)     */}
      {/* Negative top margin creates the iconic cleaner-10 bridge     */}
      {/* ============================================================ */}
      <section className="relative z-20 -mt-14 sm:-mt-16 lg:-mt-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Card 1: Erfaring & Grundighed */}
          <div className="rounded-3xl bg-white p-7 sm:p-8 shadow-xl shadow-slate-900/10 border border-slate-100 flex flex-col justify-between transition-transform duration-300 hover:-translate-y-1">
            <div>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-[#f05a28] mb-5 shadow-2xs">
                <i className="fa-solid fa-shield-check text-2xl" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 font-display mb-2">
                Grundighed & stabilitet
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                Vi møder altid op til aftalt tid, følger faste rutiner og går aldrig på kompromis med kvaliteten af vores rengøringsarbejde.
              </p>
            </div>
            <div className="pt-4 mt-2 border-t border-slate-100 flex items-center text-xs font-semibold text-[#f05a28]">
              <span>Kvalitet hver gang</span>
            </div>
          </div>

          {/* Card 2: Fleksibilitet */}
          <div className="rounded-3xl bg-white p-7 sm:p-8 shadow-xl shadow-slate-900/10 border border-slate-100 flex flex-col justify-between transition-transform duration-300 hover:-translate-y-1">
            <div>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-[#f05a28] mb-5 shadow-2xs">
                <i className="fa-solid fa-calendar-check text-2xl" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 font-display mb-2">
                Fleksible aftaler
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                Hver kunde har unikke ønsker. Vi sammensætter aftaler efter dine tidsplaner – uanset om det er ugentligt, hver 14. dag eller ved behov.
              </p>
            </div>
            <div className="pt-4 mt-2 border-t border-slate-100 flex items-center text-xs font-semibold text-[#f05a28]">
              <span>Tilpasset dit behov</span>
            </div>
          </div>

          {/* Card 3: Klinikker & Spidskompetence */}
          <div className="relative rounded-3xl bg-white p-7 sm:p-8 shadow-xl shadow-slate-900/10 border-2 border-amber-300/60 ring-1 ring-amber-100 flex flex-col justify-between transition-transform duration-300 hover:-translate-y-1">
            <div className="absolute top-4 right-4 sm:top-5 sm:right-5 z-20 text-amber-400 drop-shadow-sm flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-amber-50/95 border border-amber-300/80 shadow-2xs" aria-label="Fremhævet spidskompetence" title="Fremhævet spidskompetence">
              <i className="fa-solid fa-star text-xl sm:text-2xl text-amber-400" aria-hidden="true" />
            </div>
            <div>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-[#f05a28] mb-5 shadow-2xs">
                <i className="fa-solid fa-award text-2xl" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 font-display mb-2">
                Særlig erfaring m. klinikker
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                Vi har mangeårig specifik erfaring med rengøring af klinikker og behandlingsmiljøer, hvor præsentabilitet og orden er afgørende.
              </p>
            </div>
            <div className="pt-4 mt-2 border-t border-amber-100/70 flex items-center justify-between text-xs font-semibold text-[#f05a28]">
              <span>Vores spidskompetence</span>
              <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 font-medium">
                <i className="fa-solid fa-sparkles text-[10px]" aria-hidden="true" />
                Fokusområde
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 3: YDELSER PÅ FORSIDEN (WHITE CONTENT CANVAS)       */}
      {/* ============================================================ */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-4 py-1 text-xs font-semibold uppercase tracking-wider text-[#f05a28] mb-3">
              Vores rengøringsydelser
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
              Skræddersyede rengøringsløsninger
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              Vi tilbyder et bredt spektrum af professionelle rengøringsydelser til både private hjem, virksomheder og klinikker.
            </p>
          </div>

          {/* Special Featured Banner: KLINIK RENGØRING (Highlight as requested in brief) */}
          <div className="mb-12 sm:mb-16 rounded-3xl border border-[#0d3835]/15 bg-gradient-to-br from-[#0d3835] via-[#10433f] to-[#092826] p-8 sm:p-14 lg:p-16 xl:p-20 text-white shadow-xl relative overflow-hidden">
            {/* Background dot pattern inside card */}
            <div 
              className="absolute inset-0 pointer-events-none opacity-10 bg-[radial-gradient(#ffffff_1.5px,transparent_1.5px)] [background-size:18px_18px]" 
              aria-hidden="true" 
            />

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7 space-y-5">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#f05a28] px-3.5 py-1 text-xs font-bold text-white shadow-sm">
                  <i className="fa-solid fa-star text-[11px]" aria-hidden="true" />
                  Vores spidskompetence – Stor erfaring inden for klinikker
                </div>
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white font-display">
                  Rengøring af klinikker
                </h3>
                <p className="text-emerald-100/90 leading-relaxed text-sm sm:text-base">
                  Rengøring af klinikker kræver særlig grundighed og sans for detaljen. Havdrup Rengøring har stor erfaring med rengøring af klinikker og behandlingsmiljøer, hvor rene og præsentable omgivelser er en vigtig del af den daglige drift.
                </p>
                <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <Link
                    to="/klinik-rengoering"
                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-[#f05a28] px-6 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-[#db4d1d] transition-colors cursor-pointer text-center"
                  >
                    <span>Læs mere om klinikrengøring</span>
                    <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                  <Link
                    to="/kontakt"
                    className="inline-flex w-full sm:w-auto items-center justify-center text-sm font-semibold text-white hover:text-amber-300 underline underline-offset-4 decoration-white/40 hover:decoration-amber-300 transition-colors cursor-pointer text-center py-2"
                  >
                    Få et tilbud til din klinik
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="overflow-hidden rounded-2xl bg-white/10 aspect-16/10 shadow-lg border border-white/15">
                  <img
                    src={imageUrl(
                      '/_assets/tandlaegeklinik-tandlaegestol-rent.jpg',
                      'width=800,quality=85,format=auto'
                    )}
                    alt="Professionel rengøring af tandlægeklinik med ren tandlægestol og behandlerrum"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 4 Regular Service Cards Grid with Cleaner-10 Aesthetics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Card 1: Privat rengøring */}
            <div className="flex flex-col rounded-3xl border border-slate-200/80 bg-white p-6 shadow-lg shadow-slate-100 hover:shadow-xl hover:border-slate-300 transition-all duration-200 group">
              <div className="overflow-hidden rounded-2xl aspect-16/10 mb-5 relative bg-slate-100">
                <img
                  src={imageUrl(
                    '/_assets/shutterstock-2479566829.jpg',
                    'width=600,quality=80,format=auto'
                  )}
                  alt="Privat rengøring"
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <h3 className="text-xl font-bold tracking-tight text-slate-900 font-display mb-2">
                Privat rengøring
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed flex-1 mb-6">
                Få mere tid i hverdagen med professionel rengøring af hjemmet. Vi tilpasser rengøringen efter boligens størrelse, dine ønsker og hvor ofte du ønsker hjælp.
              </p>
              <Link
                to="/privat-rengoering"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#f05a28] hover:text-[#db4d1d] transition-colors cursor-pointer mt-auto pt-2"
              >
                <span>Læs om privat rengøring</span>
                <i className="fa-regular fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>

            {/* Card 2: Erhvervsrengøring */}
            <div className="flex flex-col rounded-3xl border border-slate-200/80 bg-white p-6 shadow-lg shadow-slate-100 hover:shadow-xl hover:border-slate-300 transition-all duration-200 group">
              <div className="overflow-hidden rounded-2xl aspect-16/10 mb-5 relative bg-slate-100">
                <img
                  src={imageUrl(
                    '/_assets/shutterstock-2200915291.jpg',
                    'width=600,quality=80,format=auto'
                  )}
                  alt="Erhvervsrengøring"
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <h3 className="text-xl font-bold tracking-tight text-slate-900 font-display mb-2">
                Erhvervsrengøring
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed flex-1 mb-6">
                Et rent arbejdsmiljø gør en forskel for både medarbejdere, kunder og besøgende. Vi tilbyder fleksibel erhvervsrengøring, der tilpasses virksomhedens lokaler og behov.
              </p>
              <Link
                to="/erhvervsrengoering"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#f05a28] hover:text-[#db4d1d] transition-colors cursor-pointer mt-auto pt-2"
              >
                <span>Læs om erhvervsrengøring</span>
                <i className="fa-regular fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>

            {/* Card 3: Hovedrengøring */}
            <div className="flex flex-col rounded-3xl border border-slate-200/80 bg-white p-6 shadow-lg shadow-slate-100 hover:shadow-xl hover:border-slate-300 transition-all duration-200 group">
              <div className="overflow-hidden rounded-2xl aspect-16/10 mb-5 relative bg-slate-100">
                <img
                  src={imageUrl(
                    '/_assets/shutterstock-2410613279.jpg',
                    'width=600,quality=80,format=auto'
                  )}
                  alt="Hovedrengøring"
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <h3 className="text-xl font-bold tracking-tight text-slate-900 font-display mb-2">
                Hovedrengøring
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed flex-1 mb-6">
                Når boligen eller virksomheden trænger til en ekstra grundig gennemgang, tilbyder vi hovedrengøring med fokus på de områder, der normalt ikke bliver taget i den almindelige rengøring.
              </p>
              <Link
                to="/hovedrengoering"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#f05a28] hover:text-[#db4d1d] transition-colors cursor-pointer mt-auto pt-2"
              >
                <span>Læs om hovedrengøring</span>
                <i className="fa-regular fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>

            {/* Card 4: Flytterengøring */}
            <div className="flex flex-col rounded-3xl border border-slate-200/80 bg-white p-6 shadow-lg shadow-slate-100 hover:shadow-xl hover:border-slate-300 transition-all duration-200 group">
              <div className="overflow-hidden rounded-2xl aspect-16/10 mb-5 relative bg-slate-100">
                <img
                  src={imageUrl(
                    '/_assets/shutterstock-2367885307.jpg',
                    'width=600,quality=80,format=auto'
                  )}
                  alt="Flytterengøring"
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <h3 className="text-xl font-bold tracking-tight text-slate-900 font-display mb-2">
                Flytterengøring
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed flex-1 mb-6">
                Gør flytningen lettere med en grundig rengøring af boligen. Vi hjælper med at gøre rummene rene og præsentable, så du kan koncentrere dig om resten af flytningen.
              </p>
              <Link
                to="/flytterengoering"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#f05a28] hover:text-[#db4d1d] transition-colors cursor-pointer mt-auto pt-2"
              >
                <span>Læs om flytterengøring</span>
                <i className="fa-regular fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 4: SECONDARY DARK CANVAS CONTAINER (DIVI CLEANER-10) */}
      {/* Rounded top/bottom dark frame enclosing about/values showcase */}
      {/* ============================================================ */}
      <section className="relative px-4 sm:px-6 lg:px-8 py-12">
        <div className="mx-auto max-w-7xl rounded-3xl bg-[#0d3835] text-white p-8 sm:p-14 lg:p-20 relative overflow-hidden shadow-2xl">
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            {/* Visual Media Container (cleaner-10 large inset off-white content card) */}
            <div className="lg:col-span-5 order-2 lg:order-1 flex flex-col">
              <div className="relative overflow-hidden rounded-3xl p-0 shadow-2xl border border-white/20 h-full w-full min-h-[300px]">
                <img
                  src={imageUrl(
                    '/_assets/shutterstock-2441536685.jpg',
                    'width=800,quality=85,format=auto'
                  )}
                  alt="Professionel rengøring og specialiseret tekstilrens"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Text Side (Built on the exact brief copy) */}
            <div className="lg:col-span-7 order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-[#154e4a]/60 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-200">
                Om virksomheden
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white font-display">
                Lidt om Havdrup Rengøring
              </h2>

              <div className="space-y-4 text-emerald-100/90 leading-relaxed text-sm sm:text-base font-light">
                <p>
                  Hos Havdrup Rengøring tilbyder vi fleksibel og omhyggelig rengøring til både private hjem, erhvervsvirksomheder og klinikker.
                </p>

                <div className="rounded-2xl border border-emerald-400/20 bg-[#154e4a]/50 p-5 backdrop-blur-xs">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-300 mb-3">
                    Vi tilbyder blandt andet:
                  </p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm text-white">
                    <li className="flex items-center gap-2.5">
                      <i className="fa-solid fa-circle-check text-[#f05a28] text-sm" aria-hidden="true" />
                      <span>Privat rengøring</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <i className="fa-solid fa-circle-check text-[#f05a28] text-sm" aria-hidden="true" />
                      <span>Erhvervsrengøring</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <i className="fa-solid fa-circle-check text-[#f05a28] text-sm" aria-hidden="true" />
                      <span>Hovedrengøring</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <i className="fa-solid fa-circle-check text-[#f05a28] text-sm" aria-hidden="true" />
                      <span>Flytterengøring</span>
                    </li>
                    <li className="flex items-center gap-2.5 col-span-1 sm:col-span-2 font-medium text-amber-200">
                      <i className="fa-solid fa-star text-[#f05a28] text-sm" aria-hidden="true" />
                      <span>Rengøring af klinikker – vi har mest erfaring indenfor klinikker</span>
                    </li>
                  </ul>
                </div>

                <p>
                  Vi tilpasser rengøringen efter den enkelte kundes behov og aftaler. Vi er fleksible og gør os umage med hver eneste opgave – uanset om det drejer sig om en mindre privat bolig eller større erhvervslokaler.
                </p>
                <p>
                  Vi ved, at behovet for rengøring ikke er ens fra kunde til kunde. Derfor tager vi udgangspunkt i den konkrete opgave og laver en aftale, der passer til både lokalerne og dine ønsker.
                </p>
                <p>
                  For os handler god rengøring ikke kun om et flot resultat. Det handler også om at være til at stole på, overholde de aftaler der bliver lavet og gøre sig umage – hver gang.
                </p>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Link
                  to="/om-os"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-[#f05a28] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#db4d1d] shadow-md transition-colors cursor-pointer text-center"
                >
                  <span>Læs mere om os</span>
                  <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <a
                  href="tel:31341632"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 text-sm font-semibold text-white hover:text-amber-300 underline underline-offset-4 decoration-white/40 hover:decoration-amber-300 transition-colors text-center py-2"
                >
                  <i className="fa-regular fa-phone text-[#f05a28]" aria-hidden="true" />
                  <span className="no-break">Ring til os: 31 34 16 32</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 5: PRIS OG TILBUD SEKTION (WARM & CLEAR MESSAGING)   */}
      {/* ============================================================ */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-orange-100 bg-gradient-to-br from-orange-50/50 via-white to-amber-50/30 p-8 sm:p-14 lg:p-16 shadow-xl shadow-orange-950/5">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-orange-100/80 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#f05a28]">
                Gennemskuelige og individuelle aftaler
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
                Hvad koster rengøring?
              </h2>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                Prisen afhænger blandt andet af opgavens størrelse, omfang, hvor ofte der skal gøres rent og de konkrete ønsker til rengøringen. Derfor tager vi udgangspunkt i den enkelte opgave, så du får en løsning, der passer til dit behov.
              </p>
              <p className="text-sm text-slate-600">
                Kontakt os gerne for at fortælle lidt om opgaven, så vi kan tage en snak om mulighederne.
              </p>

              <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#f05a28] px-7 py-4 text-base font-semibold text-white shadow-md hover:bg-[#db4d1d] transition-colors cursor-pointer w-full sm:w-auto text-center"
                >
                  <span>Få et tilbud på rengøring</span>
                  <i className="fa-regular fa-arrow-right text-sm" aria-hidden="true" />
                </Link>

                <a
                  href="tel:31341632"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-7 py-4 text-base font-semibold text-slate-800 hover:bg-slate-50 transition-colors w-full sm:w-auto cursor-pointer text-center"
                >
                  <i className="fa-regular fa-phone text-[#f05a28]" aria-hidden="true" />
                  <span className="no-break">Ring på 31 34 16 32</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
